import React, { useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  createStyles,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import _ from "lodash";

import { CustomTheme } from "../../../../types/customTheme";
import SwitchCommon from "../../../../components/switches/SwitchCommon";
import ProductList from "./ProductList";
// The category dropdown is shared with the loyalty reward mapping screen.
import CategoryMenuDropDown from "../../../PrestoExpressCustomerApp/pages/RewardListPage/CategoryMenuDropDown";

const useStyles = makeStyles<CustomTheme>((theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(1.5),
    },
    /* The title and the menu controls stay exactly where they start while the
       product list scrolls underneath them. `top` is set inline to the place
       the header rests at, so it does not move at all while scrolling, and it
       never slides over the fixed app bar. The page keeps its own single
       scrollbar - no extra scroll area is added for the list. */
    stickyHeader: {
      position: "sticky",
      zIndex: 3,
      backgroundColor: theme.palette.background.default,
      paddingTop: "12px",
      paddingBottom: "8px",
    },
    /* The department title sticks right under the header, so it is never cut
       in half by it. Its `top` is set inline as well. */
    categoryTitle: {
      position: "sticky",
      zIndex: 2,
      backgroundColor: theme.palette.background.default,
      paddingBottom: "4px",
      fontSize: "20px",
      [theme.breakpoints.up("sm")]: {
        fontSize: "32px",
      },
    },
  }),
);

export interface ThirdPartyPublishNodeProps {
  isLoading: boolean;
  menuNodes: any;
  departmentList: any;
  selectedCatMenuTitle: any;
  handleChangeCatMenu: (event: any) => void;
  updatingProductIds: any;
  handleSelectProduct: (event: any, item: any) => void;
}

/**
 * ThirdPartyPublishNode Component
 *
 * Shows the full product list of the selected menu, grouped by department.
 * A card is highlighted when the product is published to the 3rd party
 * (`isDeliverectPublished`), and clicking one flips that flag.
 */
const ThirdPartyPublishNode: React.FunctionComponent<
  ThirdPartyPublishNodeProps
> = ({
  isLoading,
  menuNodes,
  departmentList,
  selectedCatMenuTitle,
  handleChangeCatMenu,
  updatingProductIds,
  handleSelectProduct,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const elementsByTitle: any = useRef({});
  const headerRef: any = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [stickyTop, setStickyTop] = useState(0);
  const [appBarGap, setAppBarGap] = useState(0);
  const theme = useTheme<CustomTheme>();
  const classes = useStyles();
  const maxWidthCustom = useMediaQuery(theme.breakpoints.down("1280"));

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => setAnchorEl(event.currentTarget);
  const open = Boolean(anchorEl);

  /* Measures the pinned header so the department titles can stick right below
     it instead of sliding underneath it. */
  useEffect(() => {
    const updateHeaderHeight = () => {
      setHeaderHeight(headerRef.current ? headerRef.current.offsetHeight : 0);
    };
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, [isLoading, menuNodes, departmentList, appBarGap]);

  /* Measured once, while the page is still at the top and before the offsets
     below are applied:
       - stickyTop  : the place the header rests at, used as its sticky `top`
                      so it does not move at all when the page is scrolled.
       - appBarGap  : the space between the bottom of the fixed app bar and the
                      header. The header is stretched upwards over that gap
                      (negative margin + the same amount of padding), so the
                      product cards cannot be seen through it while scrolling,
                      and the header content itself stays exactly where it is. */
  useEffect(() => {
    if (stickyTop || !headerRef.current || window.scrollY !== 0) return;
    const headerTop = headerRef.current.getBoundingClientRect().top;
    const appBar: any = document.querySelector(".MuiAppBar-root");
    const appBarBottom = appBar ? appBar.getBoundingClientRect().bottom : 0;
    setStickyTop(headerTop);
    setAppBarGap(Math.max(headerTop - appBarBottom, 0));
  }, [isLoading, stickyTop]);

  const handleButtonClickMobile = (title: any) => {
    const targetElement = elementsByTitle.current[title];
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  /* Keeps only the published products when the "Show only selected" switch is
     on. */
  const filterDepartments = () => {
    if (!showSelectedOnly) {
      return departmentList;
    }

    return departmentList
      .map((department: any) => ({
        ...department,
        product: department.product.filter(
          (product: any) => product.isDeliverectPublished,
        ),
      }))
      .filter((department: any) => department.product.length > 0);
  };

  const filteredDepartmentList = filterDepartments();

  return (
    <div>
      <div
        className={classes.stickyHeader}
        ref={headerRef}
        style={{
          top: stickyTop - appBarGap,
          marginTop: -appBarGap,
          paddingTop: 12 + appBarGap,
        }}
      >
        <Typography variant="h4" align="left">
          3rd Party Publish
        </Typography>
        <Grid container spacing={2} className={classes.root}>
          <Grid item xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {!_.isEmpty(menuNodes) && selectedCatMenuTitle && (
                  <CategoryMenuDropDown
                    categoryMenuList={menuNodes}
                    selectedCatMenuTitle={selectedCatMenuTitle}
                    handleChangeCatMenu={handleChangeCatMenu}
                  />
                )}
                {!_.isEmpty(menuNodes) && departmentList && (
                  <>
                    <IconButton
                      id="basic-button"
                      style={{
                        fontSize: 11,
                        marginLeft: 12,
                        backgroundColor:
                          theme.palette.background.entity_background,
                      }}
                      onClick={handleClick}
                    >
                      {open ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      MenuListProps={{
                        "aria-labelledby": "basic-button",
                      }}
                      PaperProps={{
                        elevation: 0,
                        style: {
                          borderRadius: 10,
                          border: `1px solid ${theme.palette.background.entity_border}`,
                        },
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {departmentList.map((department: any) => (
                        <MenuItem
                          key={department.id}
                          onClick={() => {
                            handleButtonClickMobile(
                              `department ${department.id}`,
                            );
                            handleClose();
                          }}
                          style={{
                            textTransform: "none",
                            display: "flex",
                            justifyContent: "start",
                          }}
                        >
                          <Typography>{department.name}</Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  style={{ marginRight: "0px" }}
                  control={<SwitchCommon />}
                  checked={showSelectedOnly}
                  onChange={(e: any) => setShowSelectedOnly(e.target.checked)}
                  label="Show only selected"
                  labelPlacement="start"
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      {isLoading ? (
        <div
          style={{
            marginTop: "14%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="secondary" disableShrink />
        </div>
      ) : filteredDepartmentList.length > 0 ? (
        filteredDepartmentList.map((category: any, index: number) => (
          <div key={category.id} style={{ marginTop: "12px" }}>
            <Typography
              align="left"
              className={classes.categoryTitle}
              style={{
                top: stickyTop - appBarGap + headerHeight,
                scrollMarginTop: stickyTop - appBarGap + headerHeight,
              }}
              ref={(ref) =>
                (elementsByTitle.current[`department ${category.id}`] = ref)
              }
            >
              {category.name}
            </Typography>
            <Grid
              container
              style={{
                marginTop: index === 0 && maxWidthCustom ? "12px" : "0px",
              }}
            >
              <ProductList
                products={category.product}
                handleSelectProduct={handleSelectProduct}
                updatingProductIds={updatingProductIds}
              />
            </Grid>
          </div>
        ))
      ) : (
        <div
          style={{
            width: "100%",
            marginTop: "16%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="h3">No Menus Available.</Typography>
        </div>
      )}
    </div>
  );
};

export default ThirdPartyPublishNode;
